import {
  Injectable,
  inject,
  signal,
  effect,
  DestroyRef,
  resource,
} from '@angular/core';
import { interval } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ApiService } from './api.service';
import { AuthService } from '@core/services/auth.service';

interface PendingCountResponse {
  count: number;
}

@Injectable({
  providedIn: 'root',
})
export class AdminBadgeService {
  private readonly apiService = inject(ApiService);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _refreshTrigger = signal(0);
  private readonly _pendingCount = signal(0);

  private readonly countResource = resource<
    PendingCountResponse,
    number | undefined
  >({
    request: () => (this.authService.isAdmin() ? this._refreshTrigger() : undefined),
    loader: async ({ abortSignal }) => {
      return await this.apiService.get<PendingCountResponse>(
        '/influencer-access/pending-count',
        { signal: abortSignal },
      );
    },
  });

  readonly pendingCount = this._pendingCount.asReadonly();

  constructor() {
    // Update the count signal only when a new value arrives, not while loading
    effect(() => {
      const isLoading = this.countResource.isLoading();
      const error = this.countResource.error();
      if (isLoading) {
        return;
      }
      if (error) {
        console.error('Admin badge count fetch failed:', error);
        return;
      }
      const data = this.countResource.value();
      const newCount = data?.count ?? 0;
      if (this._pendingCount() !== newCount) {
        this._pendingCount.set(newCount);
      }
    });

    interval(60000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this._refreshTrigger.update((v) => v + 1);
      });
  }

  refreshPendingCount(): void {
    this._refreshTrigger.update((v) => v + 1);
  }
}
